const PRINT_AT_HOME_SELECTOR = '[name="properties[Print At Home]"]';
const INPUT_SELECTORS = `
  ${PRINT_AT_HOME_SELECTOR}, 
  [name="properties[Gift Message]"], 
  [name="properties[Recipient Email]"], 
  [name="properties[From]"], 
  [name="properties[Gift Delivery Date]"]
`;

class ProductGiftSubscription extends HTMLElement {
  submitButtonSelector = '';
  /**
   * Map of input name to whether it is required
   * @type {Object.<string, boolean>}
   */
  fieldRequirements = {};

  constructor() {
    super();
    this.submitButtonSelector = this.dataset.submitButtonSelector;
    this.getRequiredFields();
    this.setupListeners();
  }

  /**
   * Check for fields that were required at load
   */
  getRequiredFields() {
    const inputs = this.querySelectorAll(INPUT_SELECTORS);
    inputs.forEach((input) => {
      this.fieldRequirements[input.name] = input.hasAttribute('required');
    });
  }

  /**
   * Reset field error
   * @param {HTMLElement} inputEl The input element to reset
   * @return {HTMLElement} The wrapper element of the input
   */
  resetFieldError(inputEl) {
    const wrapper = inputEl.closest('.product-gift-form__field-wrapper');
    if (!wrapper) return null;
    wrapper.classList.remove('error');
    wrapper.classList.remove('error--required');
    wrapper.classList.remove('error--message');
    return wrapper;
  }

  /**
   * Set field error
   * @param {HTMLElement} wrapperEl The wrapper element of the input
   * @param {'error--required' | 'error--message'} errorType The type of error
   */
  setFieldError(wrapperEl, errorType) {
    if (!wrapperEl) return;
    wrapperEl.classList.add('error');
    wrapperEl.classList.add(errorType);
  }

  /**
   * Validate form elements
   * @param {Event} evt The event object from
   * @return {boolean} `true` if form is valid, `false` otherwise
   */
  validate(evt) {
    let valid = true;
    const inputs = this.querySelectorAll(INPUT_SELECTORS);
    inputs.forEach((input) => {
      const wrapper = this.resetFieldError(input);
      if (!input.checkValidity()) {
        if (input.hasAttribute('required') && input.value.trim() === '') {
          this.setFieldError(wrapper, 'error--required');
        } else {
          this.setFieldError(wrapper, 'error--message');
        }
        valid = false;
      }
    });
    if (!valid) evt.preventDefault();
    return valid;
  }

  /**
   * Toggles input displays based on the print at home checkbox
   * @param {boolean} checked Whether the print at home checkbox is checked
   */
  togglePrintAtHome(checked) {
    const giftRecipientDetailsEl = this.querySelector('#toggle-gift-recipient-inputs');
    if (giftRecipientDetailsEl) {
      giftRecipientDetailsEl.style.display = checked ? 'none' : 'block';
    }

    const printAtHomeCheckbox = this.querySelector(PRINT_AT_HOME_SELECTOR);
    if (printAtHomeCheckbox && printAtHomeCheckbox.checked !== checked) {
      printAtHomeCheckbox.checked = checked;
    }

    const inputEls = this.querySelectorAll(INPUT_SELECTORS.split(',').slice(1).join(','));
    inputEls.forEach((input) => {
      input.required = !checked && this.fieldRequirements[`[name="${input.name}"]`];
      input.setAttribute('aria-required', input.required);
      if (!checked && input.type === 'date') {
        const today = new Date();
        const date = `${today.toLocaleString('en-US', { year: 'numeric' })}-${today.toLocaleString('en-US', {
          month: '2-digit',
        })}-${today.toLocaleString('en-US', { day: '2-digit' })}`;
        input.value = date;
      } else if (checked) {
        input.value = '';
      }
    });
  }

  /**
   * Setup event listeners
   */
  setupListeners() {
    const submitButton = document.querySelector(this.submitButtonSelector);
    if (submitButton) {
      submitButton.addEventListener('click', this.validate.bind(this));
    }

    const printAtHomeCheckbox = this.querySelector(PRINT_AT_HOME_SELECTOR);
    if (printAtHomeCheckbox) {
      printAtHomeCheckbox.addEventListener('change', (evt) => this.togglePrintAtHome(evt.target.checked));
    }
  }
}

if (!customElements.get('product-gift-subscription')) {
  customElements.define('product-gift-subscription', ProductGiftSubscription);
}
